import type GlobalStore from "@/store";
import ShopDTO from "@/dto/shop/ShopDTO";
import WaybillTemplateDTO from "@/dto/template/WaybillTemplateDTO";
import WaybillTemplateType from "@/enum/WaybillTemplateType";
import EwaybillAccountDTO from "@/dto/ewaybillAccount/EwaybillAccountDTO";
import ShippingAddressDTO from "@/dto/ShippingAddressDTO";
import {
    InstantShipPostPayInterval,
    InstantShipPrintConfig,
    OpenInstantShipTaskParams,
} from "@/services/api/instantShip";

function getEwaybillShippingAddressIds(account: EwaybillAccountDTO) {
    const addressIds: number[] = [];

    account.subscriptions?.forEach((subscription) => {
        subscription.branchAccounts?.forEach((branchAccount) => {
            branchAccount.shippingAddresses?.forEach((address) => {
                if (!addressIds.includes(address.id)) {
                    addressIds.push(address.id);
                }
            });
        });
    });

    return addressIds;
}

export function buildOpenInstantShipTaskParams(
    globalStore: typeof GlobalStore,
    shop: ShopDTO,
    postPayInterval: InstantShipPostPayInterval,
    template: WaybillTemplateDTO,
    shippingAddress: ShippingAddressDTO,
): OpenInstantShipTaskParams {
    if (!template || template.type === WaybillTemplateType.NORMAL) {
        throw new Error("极速发货必须选择电子面单模板");
    }

    const { ewaybillShippingAddressId } = template;
    const ewaybillAccount = globalStore.ewaybillAccountStore.ewaybillAccountMapping.get(ewaybillShippingAddressId);
    const ewaybillShippingAddress =
        globalStore.ewaybillAccountStore.ewaybillShippingAddressMapping.get(ewaybillShippingAddressId);
    if (!ewaybillAccount) {
        throw new Error("未找到快递单模板对应的电子面单账号，请刷新模板后重试");
    }

    if (!ewaybillShippingAddress) {
        throw new Error("未找到快递单模板对应的电子面单发货地址，请刷新模板后重试");
    }

    if (!shippingAddress) {
        throw new Error("未找到可用发货地址，请先在设置中新增发货地址");
    }

    const printConfig: InstantShipPrintConfig = {
        waybillTemplate: {
            id: template.id,
            name: template.name,
            logisticsCompany: template.logisticsCompany,
            serviceOptions: template.serviceOptions,
            printTemplateUrl: template.printTemplateUrl,
            printTemplateId: template.printTemplateId,
            templateVersion: template.templateVersion,
        },
        ewaybillAccount: {
            id: ewaybillAccount.id,
            shopId: ewaybillAccount.shopId,
            type: ewaybillAccount.type,
            name: ewaybillAccount.name,
            platformUsername: ewaybillAccount.platformUsername,
            ewaybillShippingAddressIds: getEwaybillShippingAddressIds(ewaybillAccount),
        },
        ewaybillShippingAddress: {
            id: ewaybillShippingAddress.id,
            province: ewaybillShippingAddress.province,
            city: ewaybillShippingAddress.city,
            district: ewaybillShippingAddress.district,
            town: ewaybillShippingAddress.town,
            street: ewaybillShippingAddress.street,
        },
        shippingAddress: {
            id: shippingAddress.id,
            shipperName: shippingAddress.shipperName,
            province: shippingAddress.province,
            city: shippingAddress.city,
            district: shippingAddress.district,
            street: shippingAddress.street,
            telephone: shippingAddress.telephone,
            mobile: shippingAddress.mobile,
        },
        useChildWaybillNoMode: false,
    };

    return {
        shopId: shop.id,
        postPayInterval,
        printConfig,
        disclaimerConfirmed: true,
    };
}
